/**
 * The calm lower half of the office job page (#1717, Finish Line slice 3;
 * the v6 prototype's `.bd5`): under the Finish Line sheet, ONE white work
 * sheet (devices, then the ledger) and ONE white side sheet (contact, the
 * tech brief before the visit (#1728), then
 * the conversation), side by side on a wide screen and stacked below 1100px.
 *
 * Only drawn while the per-user switch `job-page.finish-line` is on. With it
 * off, detail.tsx draws today's page untouched.
 *
 * The cards this replaces are not gone, and since job page v2 (slice 4) none
 * of them is in a drawer: All paperwork, job photos, Manage parts and Permit
 * numbers open right under the section you clicked, and the rail's Schedule &
 * Crew (Visit) and Details (Job details) sit in the side sheet, opening there.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { FinishLine } from "@/lib/finish-line";
import type { SendHistory } from "@/lib/use-send-history";
import type { OfficeProtocol } from "../fail-protocol-card";
import type {
    JobAssetRow,
    JobDetail,
    JobRepairResult,
    JobTestResult,
    LinkedEstimate,
    LinkedInvoice,
    ReportAttachment,
} from "../types";
import { CalmDevices, type CalmDeviceActions } from "./calm-devices";
import { CityFilingSection, CustomerSection, FilesSection, MoneySection, VendorsSection } from "./calm-ledger";
import { ContactBlock, Conversation } from "./calm-side";
import { TechBriefBlock } from "./tech-brief";
import { InPlace, Quiet } from "./calm-ui";
import { JobDetailsBox, VisitBox } from "./calm-visit-details";

/** The ask in one line: Markdown marks dropped, first non-empty line. */
export function askLine(summary: string | null | undefined): string | null {
    const first = (summary ?? "")
        .split("\n")
        .map((l) =>
            l
                .replace(/^[\s>#*-]+/, "")
                .replace(/[*_`]/g, "")
                .trim(),
        )
        .find((l) => l.length > 0);
    return first ?? null;
}

type Opened = "paperwork" | "photos" | "vendors" | "permit";

/** Which side box a "Job details" / "Change date or tech" asked for. */
export type JobDetailsMode = "details" | "schedule";

/**
 * Something the job-actions list (⌘K, ⋯, a pin) asked the lower half to open
 * (job page v2). `n` goes up on every ask, so asking twice opens it twice.
 */
export type CalmRequestKind =
    | "paperwork"
    | "photos"
    | "vendors"
    | "permit"
    | "files"
    | "water-authority"
    | "note"
    | "message"
    | "call"
    | "all-contacts";
export interface CalmRequest {
    kind: CalmRequestKind;
    n: number;
}

export interface CalmJobBodyProps {
    job: JobDetail;
    finishLine: FinishLine | null;
    postVisit: boolean;
    /** Nobody has been to the site yet: the ask shows as one line on top. */
    preVisit: boolean;
    isOffice: boolean;
    assets: JobAssetRow[];
    testResults: JobTestResult[];
    repairResults: JobRepairResult[];
    tradeHasFamily: boolean;
    failProtocols?: OfficeProtocol[];
    deviceActions: CalmDeviceActions;
    invoices: LinkedInvoice[];
    estimates: LinkedEstimate[];
    onSendInvoice: (invoiceId: string) => void;
    sendHistory: SendHistory | null;
    onResend?: () => void;
    documents: ReportAttachment[];
    closeoutUploads: ReportAttachment[];
    uploadedWorkOrders: ReportAttachment[];
    summaryReports: ReportAttachment[];
    reportCount: number;
    isUploading: boolean;
    onUpload: (files: FileList | null) => void;
    onDeleteDocument: (attachmentId: string) => void;
    deletePending: boolean;
    /** Opens Job details in the side sheet (Details, the ask, …). */
    onOpenDetails: () => void;
    /** A "Job details" or "Change date or tech" ask; `n` goes up each time. */
    detailsAsk?: { mode: JobDetailsMode; n: number } | null;
    /** The rail's Schedule & Crew card, shown in the side sheet as Visit. */
    visitCard?: ReactNode;
    /** Details, the WO mapping, the ask and "more about this job", for the Job details box. */
    detailsCards?: ReactNode;
    /** Bump to open Files and scroll to it (a Finish Line "paperwork" link). */
    filesNonce?: number;
    /** A job action asked for a part of the lower half (job page v2). */
    request?: CalmRequest | null;
    /** Money's + Invoice / + Estimate (the classic header's Create links). */
    newInvoiceHref?: string | null;
    newEstimateHref?: string | null;
    /** The existing cards, opened in place (job page v2). */
    paperworkCard: ReactNode;
    photosCard: ReactNode;
    vendorCards: ReactNode;
    permitCard: ReactNode;
    /** For tests and proofs. */
    now?: Date;
    initiallyOpenDevices?: string[];
    /** Job page v3: a header device tile asked for a group. */
    devicesFocus?: { group: "pass" | "fail" | "none" | "stray"; n: number } | null;
}

export function CalmJobBody(p: CalmJobBodyProps) {
    const [filesOpen, setFilesOpen] = useState(false);
    const [opened, setOpened] = useState<Set<Opened>>(new Set());
    const filesRef = useRef<HTMLDivElement | null>(null);
    const refs = {
        paperwork: useRef<HTMLDivElement | null>(null),
        photos: useRef<HTMLDivElement | null>(null),
        vendors: useRef<HTMLDivElement | null>(null),
        permit: useRef<HTMLDivElement | null>(null),
    };
    const [scrollTo, setScrollTo] = useState<{ which: Opened; n: number } | null>(null);
    const openHere = (which: Opened) => {
        setOpened((o) => new Set(o).add(which));
        setScrollTo((s) => ({ which, n: (s?.n ?? 0) + 1 }));
    };
    const close = (which: Opened) =>
        setOpened((o) => {
            const n = new Set(o);
            n.delete(which);
            return n;
        });
    const toggle = (which: Opened) => (opened.has(which) ? close(which) : openHere(which));
    // Scroll once the block has rendered.
    useEffect(() => {
        if (scrollTo) refs[scrollTo.which].current?.scrollIntoView({ behavior: "smooth", block: "start" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollTo?.n]);
    useEffect(() => {
        if (!p.filesNonce) return;
        setFilesOpen(true);
        filesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [p.filesNonce]);
    useEffect(() => {
        const r = p.request;
        if (!r) return;
        if (r.kind === "paperwork" || r.kind === "photos" || r.kind === "vendors" || r.kind === "permit") {
            openHere(r.kind);
        } else if (r.kind === "files") {
            setFilesOpen(true);
            filesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        // The rest are handled by the section that owns them (below).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [p.request?.n]);
    const cityEditNonce = p.request?.kind === "water-authority" ? p.request.n : undefined;
    const composeRequest =
        p.request && (p.request.kind === "note" || p.request.kind === "message" || p.request.kind === "call")
            ? { kind: p.request.kind, n: p.request.n }
            : undefined;

    const filing = p.finishLine?.promises.find((x) => x.id === "filing");

    return (
        <div
            className="grid items-start gap-[18px] min-[1100px]:grid-cols-[minmax(0,1fr)_340px]"
            data-testid="calm-body"
        >
            <main
                className="min-w-0 rounded-xl border bg-card px-4 pb-5 pt-1.5 shadow-sm sm:px-7"
                data-testid="calm-work-sheet"
            >
                {/* "The ask" one-liner is gone (2026-10-22): the whole of
                    "What the tech will do" sits above the work sheet now. */}
                <CalmDevices
                    assets={p.assets}
                    testResults={p.testResults}
                    repairResults={p.repairResults}
                    postVisit={p.postVisit}
                    tradeHasFamily={p.tradeHasFamily}
                    failProtocols={p.failProtocols}
                    filing={filing}
                    actions={p.deviceActions}
                    initiallyOpen={p.initiallyOpenDevices}
                    quotedDevicesNudge={!!p.job.source_estimate_id}
                    focus={p.devicesFocus}
                />
                <CityFilingSection
                    job={p.job}
                    finishLine={p.finishLine}
                    summaryReports={p.summaryReports}
                    reportCount={p.reportCount}
                    onOpenPermit={() => toggle("permit")}
                    editNonce={cityEditNonce}
                />
                {opened.has("permit") && (
                    <InPlace ref={refs.permit} id="calm-permit" title="Permit numbers" onClose={() => close("permit")}>
                        {p.permitCard}
                    </InPlace>
                )}
                <CustomerSection
                    job={p.job}
                    finishLine={p.finishLine}
                    sendHistory={p.sendHistory}
                    onResend={p.onResend}
                />
                <VendorsSection
                    jobId={p.job.id}
                    isOffice={p.isOffice}
                    hasSourceEstimate={!!p.job.source_estimate_id}
                    onManage={() => toggle("vendors")}
                />
                {opened.has("vendors") && (
                    <InPlace
                        ref={refs.vendors}
                        id="calm-vendors-open"
                        title="Parts & purchase orders"
                        onClose={() => close("vendors")}
                    >
                        {p.vendorCards}
                    </InPlace>
                )}
                <MoneySection
                    job={p.job}
                    invoices={p.invoices}
                    estimates={p.estimates}
                    onSendInvoice={p.onSendInvoice}
                    newInvoiceHref={p.newInvoiceHref}
                    newEstimateHref={p.newEstimateHref}
                />
                <div ref={filesRef}>
                    <FilesSection
                        jobId={p.job.id}
                        isWorkOrderJob={p.job.kind === "vendor_work_order"}
                        invoices={p.invoices}
                        documents={p.documents}
                        closeoutUploads={p.closeoutUploads}
                        uploadedWorkOrders={p.uploadedWorkOrders}
                        isUploading={p.isUploading}
                        onUpload={p.onUpload}
                        onDeleteDocument={p.onDeleteDocument}
                        deletePending={p.deletePending}
                        open={filesOpen}
                        onToggle={() => setFilesOpen((o) => !o)}
                        onOpenAllPaperwork={() => toggle("paperwork")}
                        onOpenPhotos={() => toggle("photos")}
                    />
                </div>
                {opened.has("photos") && (
                    <InPlace ref={refs.photos} id="calm-photos" title="Job photos" onClose={() => close("photos")}>
                        {p.photosCard}
                    </InPlace>
                )}
                {p.job.kind === "vendor_work_order" && !opened.has("paperwork") && (
                    <div
                        className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t py-2.5 text-[14.5px]"
                        data-testid="calm-wo-tools"
                    >
                        <b className="font-semibold text-foreground">Work order</b>
                        <Quiet tone="mute" onClick={() => openHere("paperwork")}>
                            Open the WO PDF
                        </Quiet>
                        <Quiet tone="mute" onClick={() => openHere("paperwork")}>
                            Fix template · Regenerate WO PDF
                        </Quiet>
                        <Quiet tone="mute" onClick={() => openHere("paperwork")}>
                            Use as completed WO
                        </Quiet>
                        <Quiet tone="mute" onClick={p.onOpenDetails}>
                            Review mapping
                        </Quiet>
                    </div>
                )}
                {opened.has("paperwork") ? (
                    <InPlace
                        ref={refs.paperwork}
                        id="calm-all-paperwork"
                        title="All paperwork"
                        onClose={() => close("paperwork")}
                    >
                        {p.paperworkCard}
                    </InPlace>
                ) : (
                    <button
                        type="button"
                        className="flex w-full items-baseline gap-2.5 border-t py-2.5 text-left text-[14.5px] text-foreground/80 hover:text-foreground"
                        onClick={() => openHere("paperwork")}
                        aria-expanded={false}
                        data-testid="calm-all-paperwork-fold"
                    >
                        <b className="font-semibold text-foreground">All paperwork</b>
                        <span className="text-muted-foreground">
                            reports, official reports, work orders, zip, regenerate · opens right here
                        </span>
                        <span className="ml-auto shrink-0 text-[14px] font-semibold text-primary">Show ▸</span>
                    </button>
                )}
            </main>
            <aside
                className="min-w-0 rounded-xl border bg-card shadow-sm min-[1100px]:sticky min-[1100px]:top-40 min-[1100px]:max-h-[calc(100vh-11rem)] min-[1100px]:overflow-y-auto"
                aria-label="Contact, visit, job details and conversation"
                data-testid="calm-side-sheet"
            >
                <ContactBlock
                    job={p.job}
                    allContactsNonce={p.request?.kind === "all-contacts" ? p.request.n : undefined}
                />
                {p.visitCard && (
                    <VisitBox job={p.job} ask={p.detailsAsk}>
                        {p.visitCard}
                    </VisitBox>
                )}
                {p.detailsCards && (
                    <JobDetailsBox job={p.job} ask={p.detailsAsk}>
                        {p.detailsCards}
                    </JobDetailsBox>
                )}
                {/* #1728 — before the visit: what the iPad will show and need. */}
                <TechBriefBlock jobId={p.job.id} finishLine={p.finishLine} preVisit={p.preVisit} />
                <Conversation job={p.job} finishLine={p.finishLine} now={p.now} composeRequest={composeRequest} />
            </aside>
        </div>
    );
}
